import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '../../hooks/useMutation';
import PasswordField from './PasswordField';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import { PRODUCT_DESCRIPTOR, PRODUCT_NAME, TAGLINE } from '../../config/appBranding';
import '../../pages/public/AuthForm.css';
import './LoginPage.css';

const WORKSPACE_STORAGE_KEY = 'manager-ops-workspace-slug';
const DEFAULT_WORKSPACE_SLUG = process.env.REACT_APP_DEFAULT_WORKSPACE_SLUG || (process.env.NODE_ENV === 'development' ? 'demo-company' : '');

function getInitialWorkspaceSlug() {
  try {
    const workspaceFromUrl = new URLSearchParams(window.location.search).get('workspace');
    if (workspaceFromUrl) {
      return workspaceFromUrl.trim().toLowerCase();
    }
  } catch {
    // Fall back to stored/default workspace when URL parsing is unavailable.
  }

  try {
    return window.localStorage.getItem(WORKSPACE_STORAGE_KEY) || DEFAULT_WORKSPACE_SLUG;
  } catch {
    return DEFAULT_WORKSPACE_SLUG;
  }
}

function LoginPage({ errorMessage, onLogin, systemError }) {
  const [workspaceSlug, setWorkspaceSlug] = useState(getInitialWorkspaceSlug);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  const {
    mutate: submitLogin,
    isLoading: isSubmitting,
    error: submitError
  } = useMutation(onLogin, { errorFallback: 'Unable to sign in.' });

  const formError = validationError || submitError || errorMessage || systemError;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedWorkspaceSlug = workspaceSlug.trim().toLowerCase();

    if (!normalizedWorkspaceSlug || !identifier.trim() || !password.trim()) {
      setValidationError('Enter your company workspace, username/email, and password to continue.');
      return;
    }

    setValidationError('');
    const user = await submitLogin({
      workspaceSlug: normalizedWorkspaceSlug,
      identifier: identifier.trim(),
      password
    });

    if (user) {
      try {
        window.localStorage.setItem(WORKSPACE_STORAGE_KEY, normalizedWorkspaceSlug);
      } catch {
        // Keep login working if browser storage is unavailable.
      }
    }
  };

  return (
    <div className="login-shell login-shell--split">
      <div className="login-panel">
        <div className="login-brand">
          <svg className="login-brand-mark" viewBox="0 0 48 48" role="img" aria-label={`${PRODUCT_NAME} brand mark`}>
            <rect x="4" y="4" width="40" height="40" rx="12" />
            <path d="M15 30L22 18L28 27L33 14" />
            <circle cx="34" cy="34" r="3" />
          </svg>
          <span className="login-eyebrow">{PRODUCT_DESCRIPTOR}</span>
          <h1>{PRODUCT_NAME}</h1>
          <p>
            Sign in to a private company workspace built for KPI visibility, location accountability, and executive-ready decisions.
          </p>
        </div>

        <div className="login-demo-callout" aria-label="Demo workspace preview">
          <div>
            <strong>Tour the Northstar workspace</strong>
            <span>Workspace code: demo-company</span>
          </div>
          <Link to="/login?workspace=demo-company">Use demo workspace</Link>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <FormField
            label="Company Workspace"
            htmlFor="workspace"
            required
            hint="Use the workspace code provided by your company."
          >
            <Input
              id="workspace"
              type="text"
              value={workspaceSlug}
              onChange={(event) => setWorkspaceSlug(event.target.value)}
              placeholder="your-workspace"
              autoComplete="organization"
              spellCheck="false"
              error={Boolean(validationError) && !workspaceSlug.trim()}
            />
          </FormField>

          <FormField label="Username or Email" htmlFor="email" required>
            <Input
              id="email"
              type="text"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              placeholder="name@northstarops.com or username"
              autoComplete="username"
              error={Boolean(validationError) && !identifier.trim()}
            />
          </FormField>

          <PasswordField
            id="password"
            label="Password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Enter password"
            autoComplete="current-password"
          />

          {formError ? (
            <div className="auth-form__alert auth-form__alert--error" role="alert">
              {formError}
            </div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            loadingLabel="Signing In..."
          >
            Sign In
          </Button>

          <p className="auth-form__help">
            <Link to="/forgot-password">Forgot password?</Link>
          </p>
        </form>

        <div className="login-trust-row" aria-label="Trust signals">
          <span>Tenant-scoped access</span>
          <span>Private company data</span>
          <span>No shared workspace browsing</span>
        </div>
      </div>

      <aside className="login-brand-canvas" aria-label={`${PRODUCT_NAME} brand promise`}>
        <div className="login-brand-canvas__content">
          <span>{PRODUCT_NAME}</span>
          <h2>{TAGLINE}</h2>
          <p>
            Multi-location leaders get one secure place for KPIs, attendance, approvals, and executive snapshots.
          </p>
          <div className="login-preview-card" aria-hidden="true">
            <div className="login-preview-card__header">
              <span>Northstar Operating Group</span>
              <strong>Executive Snapshot</strong>
            </div>
            <div className="login-preview-card__grid">
              <div><strong>91%</strong><span>portfolio pulse</span></div>
              <div><strong>4</strong><span>locations live</span></div>
              <div><strong>6</strong><span>watch items</span></div>
            </div>
          </div>
        </div>
        <div className="login-brand-canvas__metrics" aria-hidden="true">
          <div><strong>34</strong><span>KPI signals</span></div>
          <div><strong>10m</strong><span>starter setup</span></div>
          <div><strong>1</strong><span>private workspace</span></div>
        </div>
      </aside>
    </div>
  );
}

export default LoginPage;
